// BackendCompActivations.ts — Set6 "did the selected backend actually run?" counters, per component × backend
// (cpu/webGL/webGPU), bumped at each backend's entry fn (buildGalerkinStencil, GalerkinBuildWebGL.dispatch,
// gaussSeidelSweep, ...). Tests assert ALL_WEBGL ⇒ webGL>0, cpu=0 — the 2026-07-28 silent-CPU-fallback bug.

import type {ComponentBackend} from "./Solve/Solve_Common.js";
import type {SmootherVariant} from "./Solve/Solve_Common.js";

/** The eight `MapSettings_GPU` selector components + the `galSmoother_variant` auxiliary axis: per-backend sub-counters
 *  (`cpu / webGL / webGPU`) per component, per-variant sub-counters (`SmootherVariant` + legacy `"fourColour"` — reachable
 *  via the lex branch's else fall-through, so it gets a counter to keep the audit complete). */
export interface BackendComponentActivations {
	fineSmoother: PerBackendCount;
	galerkinBuild: PerBackendCount;
	galSmoother: PerBackendCount;
	hierarchy: PerBackendCount;
	prolong: PerBackendCount;
	residual: PerBackendCount;
	restrict: PerBackendCount;
	solve: PerBackendCount;
	/** The variant-only axis on galSmoother (orthogonal to the backend axis).  Bumped
	 *  in addition to `galSmoother.<backend>` on every coarse-smoother dispatch. */
	galSmoother_variant: PerVariantCount;
}

export interface PerBackendCount {
	cpu: number;
	webGL: number;
	webGPU: number;
}

export interface PerVariantCount {
	lex: number;
	wavefront: number;
	fourColour: number;
}

function freshPerBackend(): PerBackendCount {
	return {cpu: 0, webGL: 0, webGPU: 0};
}
function freshPerVariant(): PerVariantCount {
	return {lex: 0, wavefront: 0, fourColour: 0};
}

/** Build a fresh, all-zero activations singleton.  Exported so a test / the devtools
 *  console can construct one to swap in, and so `Reset()` can reinitialise in place. */
export function createBackendComponentActivations(): BackendComponentActivations {
	return {
		fineSmoother: freshPerBackend(),
		galerkinBuild: freshPerBackend(),
		galSmoother: freshPerBackend(),
		hierarchy: freshPerBackend(),
		prolong: freshPerBackend(),
		residual: freshPerBackend(),
		restrict: freshPerBackend(),
		solve: freshPerBackend(),
		galSmoother_variant: freshPerVariant(),
	};
}

/** The eight `MapSettings_GPU` component keys — typed helper so the increment helpers
 *  below can take a compile-time-checked component name. */
export type ComponentKey =
	| "fineSmoother" | "galerkinBuild" | "galSmoother" | "hierarchy"
	| "prolong" | "residual" | "restrict" | "solve";

/** The variant key union — `SmootherVariant` plus the legacy `"fourColour"` (kept
 *  reachable via the lex branch's else fall-through on both GPU-class backends). */
export type VariantKey = SmootherVariant | "fourColour";

// ------------------------------------------------------------
// The singleton — `window`-attached in browsers, module-scope in Node vitest.
// ------------------------------------------------------------

declare global {
	interface Window {
		terrain_backendCompActivations?: BackendComponentActivations;
	}
}

/** The actual singleton instance: `window.terrain_backendCompActivations` in browsers, module-scope var in Node vitest
 *  (no `window` global) so the same Reset / increment / inspect surface works in both envs.  Resolved lazily by
 *  {@link getActivations} so importing this module in Node doesn't reference `window`. */
let moduleSingleton: BackendComponentActivations | null = null;

/** Resolve the activations singleton (browser `window` slot OR the Node-vitest module-scope fallback), lazily creating +
 *  attaching on first call.  Tests / devtools should call this rather than reading `window.terrain_backendCompActivations`
 *  directly so the Node lane gets the module-scope instance. */
export function getActivations(): BackendComponentActivations {
	if (typeof globalThis !== "undefined") {
		const w = globalThis as unknown as Window;
		if (w?.terrain_backendCompActivations) return w.terrain_backendCompActivations;
		if (!moduleSingleton) moduleSingleton = createBackendComponentActivations();
		// Attach to `window` when it exists (browser); the Node lane has no `window` so
		// the module-scope fallback stays the source of truth there.
		if (typeof window !== "undefined") {
			(window as Window).terrain_backendCompActivations = moduleSingleton;
		}
		return moduleSingleton;
	}
	if (!moduleSingleton) moduleSingleton = createBackendComponentActivations();
	return moduleSingleton;
}

/** Reset every per-component per-backend counter AND the variant counter to 0.  Called at the top of each uniform-routing
 *  unit test so assertions see a clean slate; also callable from devtools console
 *  (`window.terrain_backendCompActivations.Reset()` — `Reset` is bound to the singleton via {@link installResetMethod}). */
export function Reset(): void {
	const a = getActivations();
	a.fineSmoother = freshPerBackend();
	a.galerkinBuild = freshPerBackend();
	a.galSmoother = freshPerBackend();
	a.hierarchy = freshPerBackend();
	a.prolong = freshPerBackend();
	a.residual = freshPerBackend();
	a.restrict = freshPerBackend();
	a.solve = freshPerBackend();
	a.galSmoother_variant = freshPerVariant();
}

/** Some devtools-console callers expect `window.terrain_backendCompActivations.Reset()` to work.  Attach `Reset` as a
 *  method on the singleton object (kept in sync with the exported {@link Reset} function — both reference the same
 *  singleton via {@link getActivations}).  Idempotent. */
export function installResetMethod(): void {
	const a = getActivations() as BackendComponentActivations & {Reset?: () => void};
	a.Reset = Reset;
}

// ------------------------------------------------------------
// Increment helpers — the per-component entry fns call these.
// ------------------------------------------------------------

/** Bump the `<component>.<backend>` counter by 1.  Hot-path call site is a single property write + increment — no allocation,
 *  no branch on singleton resolution (resolved once at module load via {@link getActivations}).  Kept as a function so the
 *  call site stays one readable line and the component-name string stays compile-time-checked via {@link ComponentKey}. */
export function recordActivation(component: ComponentKey, backend: ComponentBackend): void {
	getActivations()[component][backend]++;
}

/** Bump `galSmoother_variant.<variant>` by 1 — companion to `recordActivation("galSmoother", …)`
 *  on every coarse-smoother dispatch.  Callers that ship the legacy 4-colour variant pass
 *  `"fourColour"` (the value isn't a `SmootherVariant`, hence the widened {@link VariantKey}). */
export function recordSmootherVariant(variant: VariantKey): void {
	getActivations().galSmoother_variant[variant]++;
}

// ------------------------------------------------------------
// Cross-thread collection — main page sums worker snapshots + its own.
// ------------------------------------------------------------

/** The eight `ComponentKey`-keyed fields (the variant axis is handled separately — its sub-counter type differs).  Used by
 *  {@link snapshotActivations} / {@link sumActivationSnapshots} so the per-field walk stays in sync with the `ComponentKey`
 *  union (a one-place edit if a component is added). */
const COMPONENT_KEYS: ComponentKey[] = [
	"fineSmoother", "galerkinBuild", "galSmoother", "hierarchy",
	"prolong", "residual", "restrict", "solve",
];

/** The three variant sub-counter keys (matching {@link PerVariantCount}). */
const VARIANT_KEYS: VariantKey[] = ["lex", "wavefront", "fourColour"];

/** The three backend sub-counter keys (matching {@link PerBackendCount}). */
const BACKEND_KEYS: ComponentBackend[] = ["cpu", "webGL", "webGPU"];

/** Deep-cloned plain snapshot of THIS thread's activations singleton — safe for `postMessage` (structured clone) since every
 *  field is a plain `number`; independent of the live singleton so the main page can sum worker snapshots without racing
 *  further per-worker increments.  Workers expose this via `getBackendCompActivations` (see `TerrainWorker.ts`). */
export function snapshotActivations(): BackendComponentActivations {
	const src = getActivations();
	const out = createBackendComponentActivations();
	for (const c of COMPONENT_KEYS) {
		const s = src[c]; const o = out[c];
		for (const b of BACKEND_KEYS) o[b] = s[b];
	}
	{
		const s = src.galSmoother_variant; const o = out.galSmoother_variant;
		for (const v of VARIANT_KEYS) o[v] = s[v];
	}
	return out;
}

/** Sum activation snapshots (`snapshotActivations()` output or any plain `BackendComponentActivations`-shaped object) into a
 *  FRESH one; inputs NOT mutated, empty/missing arrays → all-zero.  Used by the main page's
 *  `terrain_collectBackendCompActivations()` window hook to merge worker snapshots with the renderer-thread singleton. */
export function sumActivationSnapshots(snapshots: BackendComponentActivations[]): BackendComponentActivations {
	const out = createBackendComponentActivations();
	for (const snap of snapshots) {
		if (!snap) continue;
		for (const c of COMPONENT_KEYS) {
			const s = snap[c]; const o = out[c];
			for (const b of BACKEND_KEYS) o[b] += s[b] ?? 0;
		}
		{
			const s = snap.galSmoother_variant; const o = out.galSmoother_variant;
			for (const v of VARIANT_KEYS) o[v] += s[v] ?? 0;
		}
	}
	return out;
}

// Eagerly install the `Reset` method on module load so `window.terrain_backendCompActivations.Reset()`
// works the first time a devtools-console caller reaches for it (without an explicit init step).
installResetMethod();
